import { useState } from "react";
import { Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { IMAGES } from "../../data/site";

function fileToDataUrl(file, maxW) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const inputCls = "w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30";
const labelCls = "block text-xs uppercase tracking-widest text-forest/60 mb-1.5";

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-none`} />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-line rounded-sm p-6 md:p-8 mb-8">
      <h3 className="font-serif text-2xl text-forest mb-6">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

export default function SettingsEditor() {
  const { settings, reload } = useSite();
  const [s, setS] = useState(() => JSON.parse(JSON.stringify(settings)));
  const [saving, setSaving] = useState(false);

  const upd = (section, key, val) => setS((p) => ({ ...p, [section]: { ...p[section], [key]: val } }));
  const updManifesto = (i, key, val) => setS((p) => {
    const m = p.home.manifesto.map((x, idx) => (idx === i ? { ...x, [key]: val } : x));
    return { ...p, home: { ...p.home, manifesto: m } };
  });
  const updPage = (pg, key, val) => setS((p) => ({ ...p, pages: { ...p.pages, [pg]: { ...p.pages[pg], [key]: val } } }));

  const onImage = async (key, e, maxW) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file, maxW);
      setS((p) => ({ ...p, images: { ...(p.images || {}), [key]: dataUrl } }));
      toast.success("Image updated — remember to Save Changes");
    } catch { toast.error("Could not read image"); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.updateSettings(s); toast.success("Settings saved"); await reload(); }
    catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const c = s.college, soc = s.social, h = s.home, stu = s.student, pg = s.pages;
  const imgs = s.images || {};
  const imageFields = [
    { key: "campus", label: "Campus (Home hero)", fallback: IMAGES.campus, maxW: 1600, ratio: "aspect-video" },
    { key: "principal", label: "Principal photo", fallback: IMAGES.principal, maxW: 800, ratio: "aspect-[4/5]" },
    { key: "gallery", label: "Gallery / Campus life", fallback: IMAGES.students, maxW: 1400, ratio: "aspect-video" },
    { key: "logo", label: "College Crest / Logo", fallback: IMAGES.logo, maxW: 500, ratio: "aspect-square" },
  ];
  const pageKeys = [["notice", "Notice"], ["course", "Course"], ["department", "Department"], ["student", "Student"], ["idcard", "ID Card"]];

  return (
    <div data-testid="settings-editor">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h2 className="font-serif text-2xl text-forest">Site Content &amp; Settings</h2>
        <button data-testid="save-settings-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-forest text-bone px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
          <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <Section title="College Information">
        <Field label="Name" value={c.name} onChange={(v) => upd("college", "name", v)} testid="set-name" />
        <Field label="Short Name" value={c.short} onChange={(v) => upd("college", "short", v)} />
        <Field label="Motto" value={c.motto} onChange={(v) => upd("college", "motto", v)} />
        <Field label="Founded" value={c.founded} onChange={(v) => upd("college", "founded", v)} />
        <Field label="University" value={c.university} onChange={(v) => upd("college", "university", v)} />
        <Field label="Affiliation" value={c.affiliation} onChange={(v) => upd("college", "affiliation", v)} />
        <Field label="Affiliation No." value={c.affNo} onChange={(v) => upd("college", "affNo", v)} />
        <Field label="Principal" value={c.principal} onChange={(v) => upd("college", "principal", v)} />
        <Field label="Location" value={c.location} onChange={(v) => upd("college", "location", v)} testid="set-location" />
        <Field label="Website" value={c.website} onChange={(v) => upd("college", "website", v)} />
        <Field label="Email" value={c.email} onChange={(v) => upd("college", "email", v)} />
        <Field label="Phone" value={c.phone} onChange={(v) => upd("college", "phone", v)} testid="set-phone" />
      </Section>

      <div className="bg-white border border-line rounded-sm p-6 md:p-8 mb-8" data-testid="images-section">
        <h3 className="font-serif text-2xl text-forest mb-6">Homepage Images</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {imageFields.map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}</label>
              <div className={`w-full ${f.ratio} rounded-sm overflow-hidden border border-line bg-bone mb-3`}>
                <img src={imgs[f.key] || f.fallback} alt={f.label} className="w-full h-full object-cover" />
              </div>
              <label className="flex items-center justify-center gap-2 border border-dashed border-forest/30 rounded-sm px-4 py-2.5 cursor-pointer hover:border-gold transition-colors text-sm text-forest">
                <Upload size={15} /> Replace image
                <input data-testid={`upload-image-${f.key}`} type="file" accept="image/*" onChange={(e) => onImage(f.key, e, f.maxW)} className="hidden" />
              </label>
            </div>
          ))}
        </div>
      </div>

      <Section title="Social Links">
        <Field label="Handle" value={soc.handle} onChange={(v) => upd("social", "handle", v)} />
        <Field label="Facebook URL" value={soc.facebook} onChange={(v) => upd("social", "facebook", v)} />
        <Field label="Instagram URL" value={soc.instagram} onChange={(v) => upd("social", "instagram", v)} />
        <Field label="YouTube URL" value={soc.youtube} onChange={(v) => upd("social", "youtube", v)} />
      </Section>

      <Section title="Home Page">
        <Field label="Hero Subtitle" value={h.heroSubtitle} onChange={(v) => upd("home", "heroSubtitle", v)} textarea testid="set-hero" />
        <Field label="Marquee Text" value={h.marquee} onChange={(v) => upd("home", "marquee", v)} />
        <Field label="Manifesto Heading" value={h.manifestoHeading} onChange={(v) => upd("home", "manifestoHeading", v)} />
        <Field label="Courses Heading" value={h.coursesHeading} onChange={(v) => upd("home", "coursesHeading", v)} />
        <div className="md:col-span-2">
          <Field label="Principal's Message" value={h.principalMessage} onChange={(v) => upd("home", "principalMessage", v)} textarea />
        </div>
        {h.manifesto.map((m, i) => (
          <div key={i} className="md:col-span-2 border-t border-line pt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={`Chapter ${m.no} Title`} value={m.title} onChange={(v) => updManifesto(i, "title", v)} />
            <Field label={`Chapter ${m.no} Text`} value={m.text} onChange={(v) => updManifesto(i, "text", v)} textarea />
          </div>
        ))}
      </Section>

      <Section title="Student Page Intro">
        <Field label="Intro Title" value={stu.introTitle} onChange={(v) => upd("student", "introTitle", v)} />
        <Field label="Intro Text" value={stu.introText} onChange={(v) => upd("student", "introText", v)} textarea />
      </Section>

      <Section title="Page Headers">
        {pageKeys.map(([key, name]) => (
          <div key={key} className="md:col-span-2 border-t border-line pt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label={`${name} — Label`} value={pg[key].label} onChange={(v) => updPage(key, "label", v)} />
            <Field label={`${name} — Title`} value={pg[key].title} onChange={(v) => updPage(key, "title", v)} />
            <Field label={`${name} — Subtitle`} value={pg[key].subtitle} onChange={(v) => updPage(key, "subtitle", v)} textarea />
          </div>
        ))}
      </Section>
    </div>
  );
}
